export const calculateDuration = (start: string, end: string): number => {
  if (!start || !end) return 0; // Évite les erreurs si un champ est vide

  const [startHours, startMinutes] = start.split(":").map(Number);
  const [endHours, endMinutes] = end.split(":").map(Number);

  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;

  const duration = (endTotalMinutes - startTotalMinutes) / 60; // Conversion en heures
  return duration > 0 ? parseFloat(duration.toFixed(2)) : 0; // Conversion en number
};

// Fonction pour convertir "DD/MM/YYYY" en "YYYY-MM-DD"
export const formatDateForInput = (dateStr: string) => {
  if (!dateStr) return "";
  const [day, month, year] = dateStr.split("/");
  return `${year}-${month}-${day}`;
};

// Fonction pour ajouter une couleur par sevice.
export const addColor = (times: any[]) => {
  return times.map((element) => {
    let color = "#FFFFFF"; // Blanc par défaut

    if (element.administrative === true) {
      color = "#FFF3A2";
    } else {
      switch (element.nameDepartment) {
        case "CAC":
          color = "#B3D9FF";
          break;
        case "EC":
          color = "#B3E6B3";
          break;
        case "SOCIAL":
          color = "#C8CFFF";
          break;
        case "JURIDIQUE":
          color = "#FFD9B3";
          break;
        case "INFORMATIQUE":
          color = "#E8E8E8";
          break;
        case "COMPLIANCE":
          color = "#FFCC99";
          break;
        case "NON RECURENTE":
          color = "#FCC0FB";
          break;
        case "SOCIAL EXCEPTIONNEL":
          color = "#afe5ff";
          break;
      }
    }

    return {
      ...element, // Garde les propriétés existantes
      color, // Ajoute la propriété `color`
    };
  });
};

// Fonction pour calculer la somme d'une propriété spécifique
export const calculateTotalHTGen = <T>(
  items: T[],
  property: keyof T,
): number => {
  // Transformer chaque élément pour convertir la propriété spécifiée en nombre flottant
  const transformedItems = items.map((item) => ({
    ...item,
    [property]: parseFloat(item[property] as any),
  }));

  // Calculer la somme des valeurs transformées
  return transformedItems.reduce(
    (total, item) => total + (item[property] as number),
    0,
  );
};

export const calculateTotalTTCGen = <T>(
  items: T[],
  property: keyof T,
  tvaExonerated: boolean,
): number => {
  return items.reduce(
    (total, item) =>
      total + (item[property] as number) * (tvaExonerated ? 1 : 1.2),
    0,
  );
};

export const addTimeByDay = (times: any[]) => {
  const filteredTimes = times?.filter((time) => time.timesLabel !== "Absences");
  const timesByDay = filteredTimes?.reduce(
    (acc: { [key: string]: { times: string[]; total: number } }, time) => {
      const date = time.date;
      const length = time.length; // Format "hh:mm"

      // Convertir en minutes
      const [hours, minutes] = length.split(":").map(Number);
      const totalMinutes = hours * 60 + minutes;

      if (!acc[date]) {
        acc[date] = { times: [], total: 0 };
      }

      acc[date].times.push(length);
      acc[date].total += totalMinutes; // Ajoute à la somme

      return acc;
    },
    {},
  );

  // Convertit le total en format "hh:mm"
  const formattedTimesByDay = Object.keys(timesByDay).reduce(
    (acc: { [key: string]: { times: string[]; total: string } }, date) => {
      const totalMinutes = timesByDay[date].total;
      const formattedTotal = `${Math.floor(totalMinutes / 60)
        .toString()
        .padStart(2, "0")}:${(totalMinutes % 60).toString().padStart(2, "0")}`;

      acc[date] = {
        times: timesByDay[date].times,
        total: formattedTotal,
      };

      return acc;
    },
    {},
  );

  return formattedTimesByDay;
};

export const displayH1Production = (id: string) => {
  switch (id) {
    case "1":
      return "Temps passés par client";
    case "2":
      return "Détails d'un service";
    case "3":
      return "Production par collaborateur";
    case "4":
      return "Honoraires clients";
    case "5":
      return "Facturation";
    case "6":
      return "Production par service";
    case "7":
      return "Temps passé par collaborateur";
    case "8":
      return "Feuille de temps";
    case "9":
      return "Paiements et tva";
    case "10":
      return "Congés et absences";
    case "11":
      return "Détail des services d'un superviseur";
    default:
      return "Etat de production";
  }
};

// Format comptable
export const formatComptableMaisPasTrop = (number: number) => {
  return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

// Fonction pour formater les noms de fichiers
export const normalizeFileName = (fileName: string) => {
  const parts = fileName.split(".");
  const extension = parts.pop();
  const namePart = parts.join(".");

  const cleanedName = namePart
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9.]/g, "");

  return `${cleanedName}.${extension}`;
};

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  ".csv": "text/csv",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".pdf": "application/pdf",
  ".xlsm": "application/vnd.ms-excel.sheet.macroEnabled.12",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xml": "application/xml",
  ".zip": "application/zip",
};

const getExtension = (fileName: string): string => {
  const lower = fileName.toLowerCase();
  return (
    Object.keys(MIME_TYPE_BY_EXTENSION).find((ext) => lower.endsWith(ext)) ?? ""
  );
};

export const getMimeTypeForFileName = (fileName: string): string | undefined => {
  const extension = getExtension(fileName);
  return MIME_TYPE_BY_EXTENSION[extension];
};

export const createMultipartFileBody = async (
  file: File,
  fieldName: string,
  fileName: string,
  mimeType: string,
): Promise<{ body: Blob; contentType: string }> => {
  const boundary = `----dca${crypto.randomUUID().replace(/-/g, "")}`;
  const encoder = new TextEncoder();
  const buffer = await file.arrayBuffer();
  const safeFileName = fileName.replace(/"/g, '\\"');

  const body = new Blob([
    encoder.encode(
      `--${boundary}\r\nContent-Disposition: form-data; name="${fieldName}"; filename="${safeFileName}"\r\nContent-Type: ${mimeType}\r\n\r\n`,
    ),
    buffer,
    encoder.encode(`\r\n--${boundary}--\r\n`),
  ]);

  return {
    body,
    contentType: `multipart/form-data; boundary=${boundary}`,
  };
};

// Fonction pour créer un blob PDF
export const createPdfBlob = (data: any) => {
  return new Blob([data], { type: "application/pdf" });
};

// Fonction pour ouvrir un PDF dans une nouvelle fenêtre
export const openPdfInNewWindow = (blob: Blob) => {
  const url = window.URL.createObjectURL(blob);
  window.open(url, "_blank");
};

export const downloadPdf = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleCheckIfDateExist = (
  from: string,
  to: string,
  datesExist: any[],
) => {
  const dates = [...datesExist];

  if (dates.length > 0) {
    return dates.some((date) => {
      // Vérifie si les dates se chevauchent
      const newFrom = new Date(from);
      const newTo = new Date(to);
      const existingFrom = new Date(date.from);
      const existingTo = new Date(date.to);

      return (
        (newFrom >= existingFrom && newFrom <= existingTo) || // La date de début est dans la période existante
        (newTo >= existingFrom && newTo <= existingTo) || // La date de fin est dans la période existante
        (newFrom <= existingFrom && newTo >= existingTo) // La nouvelle période englobe la période existante
      );
    });
  }
  return false;
};

// Fonction pour extraire le nom du fichier depuis les headers
export const getFileNameFromResponse = (response: any): string => {
  const contentDisposition = response.headers["content-disposition"];
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].replace(/['"]/g, "");
    }
  }
  // Fallback: utiliser l'ID si pas de nom de fichier
  return `template_${Date.now()}.docx`;
};

// Fonction pour obtenir la classe CSS de la colonne
export const getColumnClass = (
  columnIndex: number,
  isHeader: boolean = false,
  selectedCol: number | null,
) => {
  const baseClass =
    "border border-gray-300 px-2 py-2 text-center font-semibold z-10";
  if (selectedCol === columnIndex) {
    return `${baseClass} ${isHeader ? "bg-blue-200" : "bg-blue-100"}`;
  }
  return `${baseClass} bg-thead`;
};

export const nameShortCut = (name: string, number: number) => {
  return name.length > number ? name.slice(0, number) + "..." : name;
};

export const replacePointByComma = (value: string) => {
  return value.replace(/;/g, ",");
};

// Fonction helper pour convertir en nombre de manière sûre (gère undefined, null, NaN)
export const safeNumber = (value: any): number => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

/** Parse un montant saisi (virgule ou point décimal, espaces milliers, ex. 1 234,56 ou 1.234,56). */
export const parseAmountInput = (
  value: string | number | null | undefined,
): number => {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }
  let s = String(value).trim().replace(/\s/g, "");
  if (s === "") return NaN;
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma !== -1 && lastDot !== -1) {
    if (lastComma > lastDot) {
      s = s.replace(/\./g, "").replace(",", ".");
    } else {
      s = s.replace(/,/g, "");
    }
  } else if (lastComma !== -1) {
    s = s.replace(",", ".");
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
};

// Fonction helper pour formater les valeurs avec toFixed(2) si elles ne sont pas vides
export const formatEuroValue = (
  value: string | number | null | undefined,
): string => {
  if (value === null || value === undefined || value === "" || value === 0) {
    return "";
  }

  // Si c'est une string qui se termine par un point ou une virgule, la préserver telle quelle
  // (pour permettre la saisie en cours comme "2." ou "2,")
  if (
    typeof value === "string" &&
    (value.endsWith(".") || value.endsWith(","))
  ) {
    return value;
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return "";
  }
  return parseFloat(numValue.toFixed(2)).toString();
};

// Arrondit au supérieur sur 2 decimales, sans transformer les residus flottants de 0 en 0.01
export const roundUpAmount = (value: number) => {
  if (Math.abs(value) < 1e-9) {
    return 0;
  }
  return Math.ceil(value * 100) / 100;
};

export function afficherTousLesMois(langue = "fr-FR") {
  const formatter = new Intl.DateTimeFormat(langue, { month: "long" });

  // Crée un tableau de 12 éléments (0 à 11) et les formate
  const mois = Array.from({ length: 12 }, (_, i) => {
    // On crée une date pour le mois i, l'année n'importe pas
    return formatter.format(new Date(2024, i, 1));
  });

  return mois;
}
